import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Container,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PeopleIcon from '@mui/icons-material/People';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TuneIcon from '@mui/icons-material/Tune';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import InsightsIcon from '@mui/icons-material/Insights';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import type { Page as PageType } from 'src/types/page';

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const useScrollAnimation = (threshold = 0.2) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      // threshold 0: an element taller than the viewport can never reach
      // a non-zero ratio, so tall mobile sections stayed invisible.
      { threshold: 0 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
};

const useCountUp = (target: number, duration: number, trigger: boolean) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!trigger) {
      setValue(0);
      return;
    }
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setValue(target);
        clearInterval(timer);
      } else {
        setValue(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [trigger, target, duration]);
  return value;
};

// ---------------------------------------------------------------------------
// Keyframes
// ---------------------------------------------------------------------------

const floatDiagonal = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(120px, 80px) scale(1.2); }
  50% { transform: translate(60px, 180px) scale(0.9); }
  75% { transform: translate(-60px, 100px) scale(1.15); }
`;

const bouncePurple = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-80px, 120px) scale(1.1); }
  66% { transform: translate(100px, -60px) scale(0.95); }
`;

const breathe = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.15; }
  50% { transform: scale(1.15); opacity: 0.25; }
`;

const driftPulse = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.12; }
  50% { transform: translate(-60px, -80px) scale(1.1); opacity: 0.2; }
`;

const shimmer = keyframes`
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
`;

const voiceWave = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
`;

const pulseRing = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  100% { transform: scale(2.2); opacity: 0; }
`;

const callSlideIn = keyframes`
  0% { transform: translateX(30px); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;

const insightPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.015); }
`;

const priorityBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const rowHighlight = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const dividerGrow = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const typingCursor = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EASE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

const NAV_ITEMS = [
  { icon: <HomeOutlinedIcon sx={{ fontSize: 20 }} />, label: 'Overview', active: true },
  { icon: <CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />, label: 'Cloud' },
  { icon: <BusinessCenterOutlinedIcon sx={{ fontSize: 20 }} />, label: 'Manage' },
  { icon: <PeopleOutlineIcon sx={{ fontSize: 20 }} />, label: 'CRM' },
  { icon: <MailOutlineIcon sx={{ fontSize: 20 }} />, label: 'Outbound' },
  { icon: <CampaignOutlinedIcon sx={{ fontSize: 20 }} />, label: 'Marketing' },
  { icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />, label: 'Sales' },
  { icon: <SupportAgentIcon sx={{ fontSize: 20 }} />, label: 'Support' },
  { icon: <InsightsIcon sx={{ fontSize: 20 }} />, label: 'Integrate' },
];

const VOICE_CALL_LOG = [
  { name: 'Sarah Mitchell', time: '2 min ago', duration: '3:24', result: 'Qualified', resultColor: '#10B981', scheduled: 'Tomorrow 2pm' },
  { name: 'David Rodriguez', time: '18 min ago', duration: '2:47', result: 'Qualified', resultColor: '#10B981', scheduled: 'Friday 10am' },
  { name: 'Jennifer Lee', time: '34 min ago', duration: '1:52', result: 'Not Ready', resultColor: '#F59E0B', scheduled: 'Follow-up in 2 weeks' },
  { name: 'Mike Thompson', time: '1 hr ago', duration: '4:11', result: 'Qualified', resultColor: '#10B981', scheduled: 'Thursday 4pm' },
];

const CUSTOMER_ROWS = [
  { name: 'Sarah Mitchell', status: 'Hot', statusColor: '#F97316', footage: '150 ft', value: '$4,050', source: 'Facebook', lastContact: 'Today' },
  { name: 'James Rodriguez', status: 'Closed', statusColor: '#10B981', footage: '250 ft', value: '$6,750', source: 'Google', lastContact: 'Yesterday' },
  { name: 'Jennifer Lee', status: 'Warm', statusColor: '#8B5CF6', footage: '300 ft', value: '$8,100', source: 'Referral', lastContact: '2 days ago' },
  { name: 'Michael Thompson', status: 'Superhot', statusColor: '#06B6D4', footage: '210 ft', value: '$5,670', source: 'Facebook', lastContact: 'Today' },
  { name: 'Amanda Stevens', status: 'New', statusColor: '#3B82F6', footage: '270 ft', value: '$7,290', source: 'Website', lastContact: '3 days ago' },
];

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const glassSx = {
  background: 'rgba(255, 255, 255, 0.02)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: '16px',
};

const sectionLabel = (color: string) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  px: 2,
  py: 0.75,
  borderRadius: '100px',
  bgcolor: alpha(color, 0.1),
  border: `1px solid ${alpha(color, 0.2)}`,
  color,
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.5px',
  mb: 3,
});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Page: PageType = () => {
  // Hero loads immediately
  const [heroReady, setHeroReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setHeroReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  const voiceAgent = useScrollAnimation(0.15);
  const insights = useScrollAnimation(0.15);
  const customers = useScrollAnimation(0.1);
  const closing = useScrollAnimation(0.2);

  const pipelineVal = useCountUp(847, 1500, heroReady);
  const conversionVal = useCountUp(34, 1500, heroReady);
  const leadsVal = useCountUp(127, 1500, heroReady);

  return (
    <>
      <Box
        component="main"
        sx={{
          background: '#0a0a0b',
          minHeight: '100vh',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* ---- Ambient blobs ---- */}
        <Box
          sx={{
            position: 'fixed',
            top: '-10%',
            left: '-10%',
            width: { xs: 500, md: 800 },
            height: { xs: 500, md: 800 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(41, 112, 255, 0.2) 0%, transparent 70%)',
            filter: 'blur(80px)',
            pointerEvents: 'none',
            willChange: 'transform',
            animation: `${floatDiagonal} 12s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'fixed',
            top: '5%',
            right: '-15%',
            width: { xs: 500, md: 850 },
            height: { xs: 500, md: 850 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.18) 0%, transparent 70%)',
            filter: 'blur(90px)',
            pointerEvents: 'none',
            willChange: 'transform',
            animation: `${bouncePurple} 10s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'fixed',
            top: '40%',
            left: '30%',
            width: { xs: 400, md: 650 },
            height: { xs: 400, md: 650 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(79, 70, 229, 0.15) 0%, transparent 70%)',
            filter: 'blur(65px)',
            pointerEvents: 'none',
            willChange: 'transform',
            animation: `${breathe} 8s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'fixed',
            bottom: '-5%',
            right: '10%',
            width: { xs: 350, md: 500 },
            height: { xs: 350, md: 500 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.12) 0%, transparent 70%)',
            filter: 'blur(70px)',
            pointerEvents: 'none',
            willChange: 'transform',
            animation: `${driftPulse} 14s ease-in-out infinite`,
            zIndex: 0,
          }}
        />

        {/* ================================================================ */}
        {/* SECTION 1 — HERO                                                 */}
        {/* ================================================================ */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            pt: { xs: 8, md: 12 },
            pb: { xs: 4, md: 6 },
          }}
        >
          <Container maxWidth="lg">
            <Stack alignItems="center" spacing={3}>
              {/* Badge */}
              <Box
                sx={{
                  ...sectionLabel('#2970FF'),
                  opacity: heroReady ? 1 : 0,
                  transform: heroReady ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ${EASE} 0.2s`,
                }}
              >
                <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
                Admin Dashboard
              </Box>

              {/* Headline */}
              <Typography
                component="h1"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', sm: '3.25rem', md: '4.25rem' },
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  textAlign: 'center',
                  color: '#EDF2F7',
                  opacity: heroReady ? 1 : 0,
                  transform: heroReady ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ${EASE} 0.4s`,
                }}
              >
                Run Your Lighting Business{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(135deg, #2970FF 0%, #8B5CF6 50%, #06B6D4 100%)',
                    backgroundSize: '200% auto',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: `${shimmer} 4s linear infinite`,
                  }}
                >
                  Smarter.
                </Box>
              </Typography>

              {/* Subtitle */}
              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  color: '#A0AEC0',
                  textAlign: 'center',
                  maxWidth: 580,
                  lineHeight: 1.6,
                  opacity: heroReady ? 1 : 0,
                  transform: heroReady ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ${EASE} 0.6s`,
                }}
              >
                AI voice agents, pipeline tracking, and customer management — all in one dashboard.
              </Typography>
            </Stack>

            {/* ---- Dashboard Preview Mockup ---- */}
            <Box
              sx={{
                mt: { xs: 5, md: 7 },
                mb: { xs: 4, md: 8 },
                opacity: heroReady ? 1 : 0,
                transform: heroReady ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.96)',
                transition: `all 0.9s ${EASE} 0.8s`,
              }}
            >
              <Box
                sx={{
                  borderRadius: '16px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  bgcolor: 'rgba(255,255,255,0.02)',
                  overflow: 'hidden',
                  boxShadow: `0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05), 0 0 60px ${alpha('#2970FF', 0.08)}`,
                }}
              >
                {/* Top nav bar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: 'rgba(255,255,255,0.015)',
                  }}
                >
                  {/* Logo */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 3 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        bgcolor: alpha('#2970FF', 0.15),
                        border: `1px solid ${alpha('#2970FF', 0.25)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#2970FF' }}>R</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#EDF2F7', letterSpacing: '-0.3px', display: { xs: 'none', sm: 'block' } }}>
                      Relight
                    </Typography>
                  </Stack>

                  {/* Search */}
                  <Box
                    sx={{
                      flex: 1,
                      maxWidth: 280,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '8px',
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 16, color: alpha('#fff', 0.25) }} />
                    <Typography sx={{ fontSize: '0.75rem', color: alpha('#fff', 0.2) }}>Search customers...</Typography>
                  </Box>

                  <Box sx={{ flex: 1 }} />

                  {/* Right actions */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}>
                      <NotificationsNoneIcon sx={{ fontSize: 18, color: alpha('#fff', 0.4) }} />
                    </Box>
                    <Box sx={{ width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}>
                      <AccountCircleOutlinedIcon sx={{ fontSize: 18, color: alpha('#fff', 0.4) }} />
                    </Box>
                  </Stack>
                </Box>

                {/* Main area: sidebar + content */}
                <Stack direction="row" sx={{ minHeight: { xs: 320, sm: 380, md: 440 } }}>
                  {/* Sidebar */}
                  <Box
                    sx={{
                      width: { xs: 56, sm: 68 },
                      borderRight: '1px solid rgba(255,255,255,0.06)',
                      py: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {NAV_ITEMS.map((item, i) => (
                      <Box
                        key={item.label}
                        sx={{
                          width: item.active ? { xs: 44, sm: 52 } : { xs: 36, sm: 42 },
                          height: item.active ? { xs: 44, sm: 52 } : { xs: 36, sm: 42 },
                          borderRadius: '10px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.25,
                          transition: 'all 0.2s ease',
                          cursor: 'default',
                          opacity: heroReady ? 1 : 0,
                          transform: heroReady ? 'translateX(0)' : 'translateX(-20px)',
                          transitionDelay: `${1 + i * 0.05}s`,
                          ...(item.active
                            ? {
                                bgcolor: alpha('#2970FF', 0.15),
                                border: `1px solid ${alpha('#2970FF', 0.3)}`,
                                boxShadow: `0 4px 12px ${alpha('#2970FF', 0.15)}`,
                              }
                            : {
                                border: '1px solid transparent',
                              }),
                        }}
                      >
                        <Box sx={{ color: item.active ? '#2970FF' : alpha('#fff', 0.4), display: 'flex', '& svg': { fontSize: item.active ? 18 : 17 } }}>
                          {item.icon}
                        </Box>
                        {item.active && (
                          <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: '#2970FF', lineHeight: 1 }}>
                            {item.label}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>

                  {/* Content area - dashboard overview mockup */}
                  <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5, md: 3 }, overflow: 'hidden' }}>
                    {/* Page header */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                      <Box>
                        <Typography sx={{ fontSize: { xs: '0.875rem', md: '1.1rem' }, fontWeight: 700, color: '#EDF2F7', letterSpacing: '-0.3px' }}>
                          Welcome back, Zach
                        </Typography>
                        <Typography sx={{ fontSize: '0.6875rem', color: alpha('#fff', 0.35), mt: 0.25 }}>
                          Here&apos;s what&apos;s happening with your leads today
                        </Typography>
                      </Box>
                      <Chip
                        label="Today"
                        size="small"
                        sx={{ height: 24, fontSize: '0.6875rem', bgcolor: 'rgba(255,255,255,0.06)', color: '#A0AEC0', border: '1px solid rgba(255,255,255,0.08)', display: { xs: 'none', sm: 'flex' } }}
                      />
                    </Stack>

                    {/* Stat cards row */}
                    <Stack direction="row" spacing={1.5} sx={{ mb: 2.5, overflow: 'hidden' }}>
                      {[
                        { label: 'Pipeline Value', value: `$${pipelineVal}K`, change: '+12%', color: '#10B981' },
                        { label: 'Conversion Rate', value: `${conversionVal}%`, change: '+3.2%', color: '#8B5CF6' },
                        { label: 'Active Leads', value: String(leadsVal), change: '+18', color: '#3B82F6' },
                        { label: 'Calls Today', value: '23', change: '+5', color: '#06B6D4' },
                      ].map((stat, i) => (
                        <Box
                          key={stat.label}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            p: { xs: 1.25, md: 2 },
                            borderRadius: '12px',
                            bgcolor: 'rgba(255,255,255,0.02)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            opacity: heroReady ? 1 : 0,
                            transform: heroReady ? 'translateY(0)' : 'translateY(15px)',
                            transition: `all 0.5s ${EASE} ${1.2 + i * 0.1}s`,
                          }}
                        >
                          <Typography sx={{ fontSize: '0.625rem', color: alpha('#fff', 0.35), mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {stat.label}
                          </Typography>
                          <Stack direction="row" alignItems="baseline" spacing={0.75}>
                            <Typography sx={{ fontSize: { xs: '1rem', md: '1.35rem' }, fontWeight: 700, color: '#EDF2F7', fontVariantNumeric: 'tabular-nums' }}>
                              {stat.value}
                            </Typography>
                            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, color: stat.color, display: { xs: 'none', sm: 'block' } }}>
                              {stat.change}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>

                    {/* Mini chart + recent leads row */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                      {/* Chart placeholder */}
                      <Box
                        sx={{
                          flex: 1.5,
                          p: 2,
                          borderRadius: '12px',
                          bgcolor: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.06)',
                          opacity: heroReady ? 1 : 0,
                          transform: heroReady ? 'translateY(0)' : 'translateY(15px)',
                          transition: `all 0.5s ${EASE} 1.6s`,
                        }}
                      >
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#A0AEC0', mb: 1.5 }}>Revenue Overview</Typography>
                        {/* Fake bar chart */}
                        <Stack direction="row" alignItems="flex-end" spacing={0.6} sx={{ height: { xs: 80, md: 120 } }}>
                          {[40, 65, 45, 80, 55, 90, 70, 95, 60, 85, 75, 100].map((h, i) => (
                            <Box
                              key={i}
                              sx={{
                                flex: 1,
                                height: `${h}%`,
                                borderRadius: '4px 4px 0 0',
                                bgcolor: i === 11
                                  ? '#2970FF'
                                  : i >= 9
                                    ? alpha('#2970FF', 0.5)
                                    : alpha('#2970FF', 0.15 + (i * 0.025)),
                                transition: `height 0.6s ${EASE} ${1.8 + i * 0.05}s`,
                              }}
                            />
                          ))}
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                          <Typography sx={{ fontSize: '0.5rem', color: alpha('#fff', 0.2) }}>Jan</Typography>
                          <Typography sx={{ fontSize: '0.5rem', color: alpha('#fff', 0.2) }}>Dec</Typography>
                        </Stack>
                      </Box>

                      {/* Recent leads */}
                      <Box
                        sx={{
                          flex: 1,
                          p: 2,
                          borderRadius: '12px',
                          bgcolor: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.06)',
                          opacity: heroReady ? 1 : 0,
                          transform: heroReady ? 'translateY(0)' : 'translateY(15px)',
                          transition: `all 0.5s ${EASE} 1.7s`,
                        }}
                      >
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#A0AEC0', mb: 1.5 }}>Recent Leads</Typography>
                        <Stack spacing={1}>
                          {[
                            { name: 'Sarah M.', status: 'Hot', color: '#F97316' },
                            { name: 'James R.', status: 'Qualified', color: '#10B981' },
                            { name: 'Jennifer L.', status: 'New', color: '#3B82F6' },
                            { name: 'Mike T.', status: 'Warm', color: '#8B5CF6' },
                          ].map((lead, i) => (
                            <Stack
                              key={lead.name}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{
                                opacity: heroReady ? 1 : 0,
                                transform: heroReady ? 'translateX(0)' : 'translateX(10px)',
                                transition: `all 0.4s ${EASE} ${1.9 + i * 0.1}s`,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 22,
                                  height: 22,
                                  fontSize: '0.5rem',
                                  bgcolor: alpha(lead.color, 0.15),
                                  color: lead.color,
                                }}
                              >
                                {lead.name.split(' ').map(n => n[0]).join('')}
                              </Avatar>
                              <Typography sx={{ fontSize: '0.6875rem', color: '#EDF2F7', fontWeight: 500, flex: 1 }}>
                                {lead.name}
                              </Typography>
                              <Chip
                                label={lead.status}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.5625rem',
                                  fontWeight: 600,
                                  bgcolor: alpha(lead.color, 0.12),
                                  color: lead.color,
                                }}
                              />
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ================================================================ */}
        {/* SECTION 2 — AI VOICE AGENT                                       */}
        {/* ================================================================ */}
        <Box ref={voiceAgent.ref} sx={{ position: 'relative', zIndex: 1, py: { xs: 10, md: 16 } }}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 6, md: 8 }}
              alignItems="center"
            >
              {/* Text */}
              <Box
                sx={{
                  flex: '0 0 40%',
                  opacity: voiceAgent.isVisible ? 1 : 0,
                  transform: voiceAgent.isVisible ? 'translateX(0)' : 'translateX(-40px)',
                  transition: `all 0.8s ${EASE}`,
                }}
              >
                <Box sx={sectionLabel('#10B981')}>
                  <PhoneInTalkIcon sx={{ fontSize: 16 }} />
                  AI Voice Agent
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: '2rem', md: '2.75rem' },
                    color: '#EDF2F7',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Never Miss a Call.{' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Ever.
                  </Box>
                </Typography>
                <Typography sx={{ color: '#A0AEC0', fontSize: '1rem', lineHeight: 1.7, mb: 4 }}>
                  Your AI voice agent qualifies leads and schedules in-person quotes 24/7. Fully automated, customized to your business.
                </Typography>
                <Stack spacing={2.5}>
                  {[
                    { icon: <PhoneCallbackIcon sx={{ fontSize: 18 }} />, text: 'Answers every call — no missed opportunities', color: '#10B981' },
                    { icon: <EventAvailableIcon sx={{ fontSize: 18 }} />, text: "Doesn't take vacations or sick days", color: '#06B6D4' },
                    { icon: <TrendingUpIcon sx={{ fontSize: 18 }} />, text: 'Increase conversions with instant response', color: '#8B5CF6' },
                    { icon: <TuneIcon sx={{ fontSize: 18 }} />, text: 'Fully customized to your business & services', color: '#3B82F6' },
                  ].map((feat, i) => (
                    <Stack
                      key={feat.text}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        opacity: voiceAgent.isVisible ? 1 : 0,
                        transform: voiceAgent.isVisible ? 'translateX(0)' : 'translateX(-20px)',
                        transition: `all 0.5s ${EASE} ${0.3 + i * 0.12}s`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          bgcolor: alpha(feat.color, 0.12),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: feat.color,
                          flexShrink: 0,
                        }}
                      >
                        {feat.icon}
                      </Box>
                      <Typography sx={{ color: '#A0AEC0', fontSize: '0.9375rem' }}>{feat.text}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              {/* Voice Agent mockup */}
              <Box
                sx={{
                  flex: 1,
                  opacity: voiceAgent.isVisible ? 1 : 0,
                  transform: voiceAgent.isVisible ? 'translateX(0)' : 'translateX(40px)',
                  transition: `all 0.8s ${EASE} 0.2s`,
                }}
              >
                <Box
                  sx={{
                    ...glassSx,
                    p: { xs: 2.5, md: 3.5 },
                    boxShadow: '0 25px 60px rgba(16, 185, 129, 0.1)',
                  }}
                >
                  {/* Active call indicator */}
                  <Box
                    sx={{
                      ...glassSx,
                      p: 2.5,
                      mb: 2.5,
                      borderColor: alpha('#10B981', 0.15),
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      {/* Pulsing phone icon */}
                      <Box sx={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            bgcolor: alpha('#10B981', 0.15),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <SmartToyOutlinedIcon sx={{ fontSize: 22, color: '#10B981' }} />
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            border: `2px solid ${alpha('#10B981', 0.3)}`,
                            animation: `${pulseRing} 2s ease-out infinite`,
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            border: `2px solid ${alpha('#10B981', 0.2)}`,
                            animation: `${pulseRing} 2s ease-out infinite 0.5s`,
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#EDF2F7' }}>
                            AI Agent Active
                          </Typography>
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10B981', boxShadow: `0 0 8px ${alpha('#10B981', 0.6)}` }} />
                        </Stack>
                        <Typography sx={{ fontSize: '0.75rem', color: '#A0AEC0', mt: 0.25 }}>
                          Speaking with Sarah Mitchell — qualifying lead...
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#10B981', fontVariantNumeric: 'tabular-nums' }}>
                        1:47
                      </Typography>
                    </Stack>

                    {/* Voice waveform */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.4}
                      sx={{ mt: 2, height: 32, justifyContent: 'center' }}
                    >
                      {Array.from({ length: 40 }).map((_, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 3,
                            borderRadius: '2px',
                            bgcolor: i < 24
                              ? alpha('#10B981', 0.3 + Math.random() * 0.5)
                              : alpha('#06B6D4', 0.2 + Math.random() * 0.4),
                            height: '100%',
                            transformOrigin: 'center',
                            animation: `${voiceWave} ${0.4 + Math.random() * 0.6}s ease-in-out infinite ${Math.random() * 0.5}s`,
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Conversation preview */}
                  <Box
                    sx={{
                      ...glassSx,
                      p: 2,
                      mb: 2.5,
                      borderColor: alpha('#fff', 0.04),
                    }}
                  >
                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                      Live Transcript
                    </Typography>
                    <Stack spacing={1.5}>
                      {[
                        { speaker: 'AI', text: "Hi Sarah! I'm calling from Relight Exterior Lighting. I see you were interested in permanent eave lighting. Is this a good time?", delay: 0 },
                        { speaker: 'Sarah', text: "Yes! We just bought a new house and want the whole roofline done.", delay: 0.15 },
                        { speaker: 'AI', text: "That's great. Can you estimate the total footage? And do you have a preference for warm white or color-changing?", delay: 0.3 },
                      ].map((msg, i) => (
                        <Stack
                          key={i}
                          direction="row"
                          spacing={1.5}
                          sx={{
                            opacity: voiceAgent.isVisible ? 1 : 0,
                            animation: voiceAgent.isVisible ? `${callSlideIn} 0.5s ${EASE} ${0.6 + msg.delay}s both` : 'none',
                          }}
                        >
                          <Chip
                            label={msg.speaker}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.625rem',
                              fontWeight: 700,
                              bgcolor: msg.speaker === 'AI' ? alpha('#10B981', 0.12) : alpha('#3B82F6', 0.12),
                              color: msg.speaker === 'AI' ? '#10B981' : '#3B82F6',
                              flexShrink: 0,
                              mt: 0.25,
                            }}
                          />
                          <Typography sx={{ fontSize: '0.8125rem', color: '#A0AEC0', lineHeight: 1.5 }}>
                            {msg.text}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  {/* Recent call log */}
                  <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5, px: 0.5 }}>
                    Recent Calls
                  </Typography>
                  <Stack spacing={1}>
                    {VOICE_CALL_LOG.map((call, i) => (
                      <Box
                        key={call.name}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.5,
                          py: 1.25,
                          borderRadius: '10px',
                          bgcolor: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.05)',
                          opacity: voiceAgent.isVisible ? 1 : 0,
                          transform: voiceAgent.isVisible ? 'translateY(0)' : 'translateY(12px)',
                          transition: `all 0.4s ${EASE} ${0.8 + i * 0.1}s`,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: '0.625rem',
                            bgcolor: alpha(call.resultColor, 0.15),
                            color: call.resultColor,
                          }}
                        >
                          {call.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.8125rem', color: '#EDF2F7', fontWeight: 500 }}>
                            {call.name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6875rem', color: alpha('#fff', 0.3) }}>
                            {call.time} · {call.duration}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.25}>
                          <Chip
                            label={call.result}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.625rem',
                              fontWeight: 600,
                              bgcolor: alpha(call.resultColor, 0.12),
                              color: call.resultColor,
                            }}
                          />
                          <Typography sx={{ fontSize: '0.625rem', color: alpha('#fff', 0.3) }}>
                            {call.scheduled}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* ================================================================ */}
        {/* SECTION 3 — AI INSIGHTS                                          */}
        {/* ================================================================ */}
        <Box ref={insights.ref} sx={{ position: 'relative', zIndex: 1, py: { xs: 10, md: 16 } }}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column-reverse', md: 'row' }}
              spacing={{ xs: 6, md: 8 }}
              alignItems="center"
            >
              {/* Insight cards mockup */}
              <Box
                sx={{
                  flex: 1,
                  opacity: insights.isVisible ? 1 : 0,
                  transform: insights.isVisible ? 'translateX(0)' : 'translateX(-40px)',
                  transition: `all 0.8s ${EASE}`,
                }}
              >
                <Stack spacing={2}>
                  {[
                    {
                      icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
                      iconColor: '#8B5CF6',
                      title: '3 Hot Leads Ready to Close',
                      category: 'sales',
                      catColor: '#8B5CF6',
                      priority: '#EF4444',
                      metric: 'Est. Value: $18,900',
                      action: 'Contact these leads within 24 hours',
                    },
                    {
                      icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
                      iconColor: '#3B82F6',
                      title: 'Facebook CPL Below Target',
                      category: 'marketing',
                      catColor: '#3B82F6',
                      priority: '#F59E0B',
                      metric: 'CPL: $67 | 23 leads',
                      action: 'Consider increasing budget to scale',
                    },
                    {
                      icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
                      iconColor: '#10B981',
                      title: 'Strong Conversion Rate',
                      category: 'sales',
                      catColor: '#8B5CF6',
                      priority: '#10B981',
                      metric: '34.2% close rate',
                      action: 'Keep current strategy running',
                    },
                  ].map((insight, i) => (
                    <Box
                      key={insight.title}
                      sx={{
                        ...glassSx,
                        p: 2.5,
                        opacity: insights.isVisible ? 1 : 0,
                        transform: insights.isVisible ? 'translateY(0)' : 'translateY(30px)',
                        transition: `all 0.6s ${EASE} ${i * 0.15}s`,
                        ...(i === 0 ? { animation: `${insightPulse} 4s ease-in-out infinite` } : {}),
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            bgcolor: alpha(insight.iconColor, 0.15),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: insight.iconColor,
                            flexShrink: 0,
                          }}
                        >
                          {insight.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#EDF2F7' }}>
                              {insight.title}
                            </Typography>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: insight.priority,
                                animation: `${priorityBlink} 2s ease-in-out infinite`,
                                flexShrink: 0,
                              }}
                            />
                          </Stack>
                          <Chip
                            label={insight.category}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.625rem',
                              fontWeight: 600,
                              bgcolor: alpha(insight.catColor, 0.12),
                              color: insight.catColor,
                              mb: 1,
                            }}
                          />
                          <Box
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.04)',
                              borderRadius: '8px',
                              px: 1.5,
                              py: 0.75,
                              mb: 1,
                            }}
                          >
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#EDF2F7', fontVariantNumeric: 'tabular-nums' }}>
                              {insight.metric}
                            </Typography>
                          </Box>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <LightbulbOutlinedIcon sx={{ fontSize: 14, color: '#F59E0B' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: '#A0AEC0' }}>
                              {insight.action}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* Text */}
              <Box
                sx={{
                  flex: '0 0 38%',
                  opacity: insights.isVisible ? 1 : 0,
                  transform: insights.isVisible ? 'translateX(0)' : 'translateX(40px)',
                  transition: `all 0.8s ${EASE} 0.1s`,
                }}
              >
                <Box sx={sectionLabel('#8B5CF6')}>
                  <PsychologyIcon sx={{ fontSize: 16 }} />
                  AI Insights
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: '2rem', md: '2.75rem' },
                    color: '#EDF2F7',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  AI-Powered Business Intelligence
                </Typography>
                <Typography sx={{ color: '#A0AEC0', fontSize: '1rem', lineHeight: 1.7, mb: 4 }}>
                  Get actionable insights from your data. Know which leads to call, when to scale ads, and where to optimize.
                </Typography>

                {/* Mini stat cards */}
                <Stack spacing={1.5}>
                  {[
                    { label: 'Revenue', value: '$284K', color: '#10B981' },
                    { label: 'Conversion', value: '34.2%', color: '#8B5CF6' },
                    { label: 'Active Insights', value: '12', color: '#2970FF' },
                  ].map((stat, i) => (
                    <Box
                      key={stat.label}
                      sx={{
                        ...glassSx,
                        px: 2.5,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        opacity: insights.isVisible ? 1 : 0,
                        transform: insights.isVisible ? 'translateY(0)' : 'translateY(20px)',
                        transition: `all 0.5s ${EASE} ${0.3 + i * 0.1}s`,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.875rem', color: '#A0AEC0' }}>{stat.label}</Typography>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: stat.color, fontVariantNumeric: 'tabular-nums' }}>
                        {stat.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* ================================================================ */}
        {/* SECTION 4 — CUSTOMER MANAGEMENT                                  */}
        {/* ================================================================ */}
        <Box ref={customers.ref} sx={{ position: 'relative', zIndex: 1, py: { xs: 10, md: 16 } }}>
          <Container maxWidth="lg">
            {/* Header */}
            <Stack
              alignItems="center"
              spacing={2}
              sx={{
                mb: 6,
                opacity: customers.isVisible ? 1 : 0,
                transform: customers.isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: `all 0.8s ${EASE}`,
              }}
            >
              <Box sx={sectionLabel('#3B82F6')}>
                <PeopleIcon sx={{ fontSize: 16 }} />
                Customer Management
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  color: '#EDF2F7',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  textAlign: 'center',
                }}
              >
                Every Customer, One Dashboard
              </Typography>
              <Typography sx={{ color: '#A0AEC0', fontSize: '1rem', lineHeight: 1.7, textAlign: 'center', maxWidth: 560 }}>
                Advanced filtering, sorting, and detailed customer profiles. From first contact to installation complete.
              </Typography>
            </Stack>

            {/* Table mockup */}
            <Box
              sx={{
                ...glassSx,
                overflow: 'hidden',
                boxShadow: '0 25px 60px rgba(139, 92, 246, 0.08)',
                opacity: customers.isVisible ? 1 : 0,
                transform: customers.isVisible ? 'translateY(0) scale(1)' : 'translateY(60px) scale(0.97)',
                transition: `all 0.9s ${EASE} 0.3s`,
              }}
            >
              {/* Search bar */}
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <SearchIcon sx={{ fontSize: 18, color: alpha('#fff', 0.3) }} />
                <Typography sx={{ fontSize: '0.875rem', color: alpha('#fff', 0.25) }}>
                  Search customers...
                </Typography>
                <Box
                  sx={{
                    width: 1,
                    height: 16,
                    bgcolor: alpha('#fff', 0.4),
                    animation: `${typingCursor} 1s ease-in-out infinite`,
                  }}
                />
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  {['All Statuses', 'Facebook', 'Last 30 days'].map((filter) => (
                    <Chip
                      key={filter}
                      label={filter}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.6875rem',
                        bgcolor: 'rgba(255,255,255,0.06)',
                        color: '#A0AEC0',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Table header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.5fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr',
                  px: 3,
                  py: 1.5,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {['Name', 'Status', 'Footage', 'Est. Value', 'Source', 'Last Contact'].map((h) => (
                  <Typography key={h} sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {h}
                  </Typography>
                ))}
              </Box>

              {/* Table rows */}
              {CUSTOMER_ROWS.map((row, i) => (
                <Box
                  key={row.name}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr',
                    px: 3,
                    py: 2,
                    alignItems: 'center',
                    borderBottom: i < CUSTOMER_ROWS.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                    opacity: customers.isVisible ? 1 : 0,
                    transform: customers.isVisible ? 'translateY(0)' : 'translateY(15px)',
                    transition: `all 0.5s ${EASE} ${0.5 + i * 0.1}s`,
                    position: 'relative',
                    overflow: 'hidden',
                    ...(i === 1
                      ? {
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `linear-gradient(90deg, transparent, ${alpha('#2970FF', 0.06)}, transparent)`,
                            backgroundSize: '200% 100%',
                            animation: `${rowHighlight} 5s linear infinite`,
                            pointerEvents: 'none',
                          },
                        }
                      : {}),
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.6875rem', bgcolor: alpha(row.statusColor, 0.2), color: row.statusColor }}>
                      {row.name.split(' ').map((n) => n[0]).join('')}
                    </Avatar>
                    <Typography sx={{ fontSize: '0.875rem', color: '#EDF2F7', fontWeight: 500 }}>
                      {row.name}
                    </Typography>
                  </Stack>
                  <Chip
                    label={row.status}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      bgcolor: alpha(row.statusColor, 0.12),
                      color: row.statusColor,
                      width: 'fit-content',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.875rem', color: '#A0AEC0' }}>{row.footage}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#EDF2F7', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {row.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#A0AEC0' }}>{row.source}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#A0AEC0' }}>{row.lastContact}</Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ================================================================ */}
        {/* SECTION 5 — CLOSING                                              */}
        {/* ================================================================ */}
        <Box ref={closing.ref} sx={{ position: 'relative', zIndex: 1, py: { xs: 12, md: 20 } }}>
          <Container maxWidth="md">
            <Stack
              alignItems="center"
              spacing={3}
              sx={{
                opacity: closing.isVisible ? 1 : 0,
                transform: closing.isVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
                transition: `all 0.8s ${EASE}`,
              }}
            >
              <Typography
                component="h2"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                  color: '#EDF2F7',
                  textAlign: 'center',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                Built for Lighting Professionals
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  color: '#A0AEC0',
                  textAlign: 'center',
                  maxWidth: 480,
                }}
              >
                Everything you need to grow your lighting business.
              </Typography>

              {/* Gradient divider */}
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 4 }}>
                <Box
                  sx={{
                    height: 1,
                    maxWidth: 400,
                    background: `linear-gradient(90deg, transparent, ${alpha('#2970FF', 0.5)}, ${alpha('#8B5CF6', 0.5)}, transparent)`,
                    animation: closing.isVisible ? `${dividerGrow} 1.2s ease-out 0.5s both` : 'none',
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: alpha('#fff', 0.25),
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                Permanent Outdoor Lighting
              </Typography>
            </Stack>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Page;
